/**
 * EmojiPicker — reusable categorized emoji picker with keyword search.
 * Used in comments, chat input, and post creation.
 */
import { useMemo, useState } from 'react';
import { useTranslation } from '@wlb/shared/i18n/useTranslation';
import { EMOJI_CATEGORIES, filterEmojiCategories } from './emojiData';
import './emojiPicker.css';

interface EmojiPickerProps {
  onSelect: (emoji: string) => void;
}

export function EmojiPicker({ onSelect }: EmojiPickerProps) {
  const { t } = useTranslation();
  const [search, setSearch] = useState('');
  const [activeCategory, setActiveCategory] = useState(EMOJI_CATEGORIES[0]?.id ?? '');

  const filteredCategories = useMemo(
    () => filterEmojiCategories(search),
    [search],
  );

  return (
    <div className="emoji-picker" role="group" aria-label={t('comments.emojiPicker')}>
      <div className="emoji-picker__search-bar">
        <input
          className="emoji-picker__search"
          type="text"
          value={search}
          onChange={e => setSearch(e.target.value)}
          placeholder={t('comments.searchEmojiPlaceholder')}
          aria-label={t('comments.searchEmojiPlaceholder')}
        />
      </div>

      {!search.trim() && (
        <div className="emoji-picker__tabs" role="tablist">
          {EMOJI_CATEGORIES.map(cat => (
            <button
              key={cat.id}
              type="button"
              className={`emoji-picker__tab${activeCategory === cat.id ? ' emoji-picker__tab--active' : ''}`}
              onClick={() => setActiveCategory(cat.id)}
              role="tab"
              aria-selected={activeCategory === cat.id}
              aria-label={t(cat.labelKey)}
            >
              {cat.emojis[0]}
            </button>
          ))}
        </div>
      )}

      <div className="emoji-picker__body">
        {filteredCategories.length === 0 ? (
          <p className="emoji-picker__empty">{t('comments.searchEmojiEmpty')}</p>
        ) : (
          filteredCategories.map(cat => (
            <section
              key={cat.id}
              className="emoji-picker__section"
              id={`emoji-section-${cat.id}`}
              hidden={!search.trim() && activeCategory !== cat.id}
            >
              <h4 className="emoji-picker__section-label">{t(cat.labelKey)}</h4>
              <div className="emoji-picker__grid">
                {cat.emojis.map(emoji => (
                  <button
                    key={emoji}
                    type="button"
                    className="emoji-picker__item"
                    onClick={() => onSelect(emoji)}
                  >
                    {emoji}
                  </button>
                ))}
              </div>
            </section>
          ))
        )}
      </div>
    </div>
  );
}
